<template>
  <div class="video-container" id="video-container" ref="videoContainer" :key="video.id">
    <div class="playback-animation" id="playback-animation" ref="playbackAnimation">
      <svg class="playback-icons">
        <use class="hidden" href="#play-icon"></use>
        <use href="#pause"></use>
      </svg>
    </div>
    <video
      controls
      class="videoDom"
      id="videoDom"
      ref="videoDom"
      :key="video.id"
      :poster="apiUrl + video.posterPath"
      @play="video_play"
      @pause="video_pause"
      @loadedmetadata="video_loadedmetadata"
      @loadeddata="video_loadeddata"
      @timeupdate="video_timeupdate"
      @volumechange="video_volumechange"
      @click="video_click"
      @mouseenter="video_mouseenter"
      @mouseleave="video_mouseleave"
      @mousemove="video_mousemove"
      @ended="video_ended"
    >
      <source :src="apiUrl + video.videoPath" type="video/mp4" />
      Sorry, your browser doesn't support embedded videos.
    </video>

    <div class="next-video-preview hidden" id="next-video-preview" ref="nextVideoPreview">
      <v-img contain :src="nextVideoPoster">
        <v-avatar color="white" >
            <v-img contain :src="themeLogo" />
        </v-avatar>
      </v-img>
      <div id="message-preview">
        <h2 id="message-preview-title" color="white">
          {{$t('video.player.nextVideoMessage')}}
          <span ref="displayCountdownTimer" id="message-preview-title-info">10 {{$t('video.player.nextVideoSeconds')}}</span>
        </h2>
        <h3 v-if="nextVideo" id="message-preview-subtitle" color="white">
          {{ this.nextVideo.title }}
        </h3>
      </div>
    </div>
        <v-alert
          class="last-progress-resume"
          :value="lastProgressResumeVisible"
          border="left"
          dense
          elevation="9"
          outlined
          icon="mdi-clock-fast"
          transition="scale-transition"
        >{{$t('video.player.resumeMessage')}}</v-alert>    
    <div 
    class="video-controls hidden" 
    id="video-controls"
    ref="videoControls"
    @mouseenter="videoControls_mouseenter"
    @mouseleave="videoControls_mouseleave">
      <div class="video-progress">
        <progress ref="progressBar" id="progress-bar" value="0" min="0"></progress>
        <input 
        class="seek" 
        id="seek" 
        ref="seek"
        value="0" 
        min="0" 
        type="range" 
        step="1"
        @mousemove="updateSeekTooltip"
        @click="skipAhead"
        />
        <div class="seek-tooltip" id="seek-tooltip" ref="seekTooltip">00:00</div>
      </div>

      <div class="bottom-controls">
        <div class="left-controls">
          <button 
          id="play"
          ref="playButton"
          @click="this.togglePlay"
          class="video-button" 
          :data-title="`${this.$t('video.player.play')} (${this.$t('video.player.shortcut.space')})`" 
          >
            <svg class="playback-icons">
              <use href="#play-icon"></use>
              <use class="hidden" href="#pause"></use>
            </svg>
          </button>

          <div class="volume-controls">
            <button
              :data-title="`${this.$t('video.player.mute')} (m)`"
              class="video-button volume-button"
              id="volume-button"
              ref="volumeButton"
              @click="toggleMute"
            >
              <svg>
                <use class="hidden" href="#volume-mute" ref="volumeMute"></use>
                <use class="hidden" href="#volume-low" ref="volumeLow"></use>
                <use href="#volume-high" ref="volumeHigh"></use>
              </svg>
            </button>

            <input
              class="volume"
              id="volume"
              ref="volume"
              value="1"
              data-mute="0"
              type="range"
              max="1"
              min="0"
              step="0.01"
              @click="updateVolume"
            />
          </div>

          <div class="time">
            <time ref="timeElapsed" id="time-elapsed">00:00</time>
            <span> / </span>
            <time ref="duration" id="duration">00:00</time>
          </div>
        </div>

        <div class="right-controls">
          <switchTMG :data-title="`${autoplayEnabled ? this.$t('video.player.autoplay.activated') : this.$t('video.player.autoplay.desactivated')}`" class="switch-control" v-model="autoplayEnabled" @input="autoplayChange">
            <template v-slot:toggle-indicator-content>
               <svg>
                <use v-if="autoplayEnabled" href="#play-icon"></use>
                <use v-if="!autoplayEnabled" href="#pause"></use>
              </svg>
            </template>
          </switchTMG>
          <button
            :data-title="`${this.$t('video.player.back')} 10s (⬅️)`"
            class="video-button back-forward-button"
            id="back-forward-button"
            ref="backForwardButton"
            @click="backForward"
          >
            <svg>
              <use href="#back-forward"></use>
            </svg>
          </button>
          <button
            :data-title="`${this.$t('video.player.forward')} 10s (➡️)`"
            class="video-button fast-forward-button"
            id="fast-forward-button"
            ref="fastForwardButton"
            @click="fastForward"
          >
            <svg>
              <use href="#fast-forward"></use>
            </svg>
          </button>
          <button
            v-if="!isFirefox"
            :data-title="`${this.$t('video.player.pip')} (p)`"
            class="video-button pip-button"
            id="pip-button"
            ref="pipButton"
            @click="togglePip"
          >
            <svg>
              <use href="#pip"></use>
            </svg>
          </button>
          <button
            :data-title="`${this.$t('video.player.fullscreen')} (f)`"
            class="video-button fullscreen-button"
            id="fullscreen-button"
            ref="fullscreenButton"
          >
            <svg>
              <use href="#fullscreen"></use>
              <use href="#fullscreen-exit" class="hidden"></use>
            </svg>
          </button>
          <v-menu
            class="options-menu"
            dark
            attach=".right-controls"
            :close-on-content-click="false"
            :nudge-width="200"
            top
            left
            offset-y
            content-class="options-menu-content"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="options-menu-button" icon color="white" v-bind="attrs" v-on="on">
                <v-icon class="options-menu-icon"> mdi-dots-vertical </v-icon>
              </v-btn>
            </template>
            <v-card dark>
              <v-list dark class="options-menu-list">
                <v-list-item>
                  <v-list-item-content>
                    <v-slider
                      id="slider-button"
                      v-model="speedVideo"
                      @change="speedVideoChange"
                      hide-details
                      dark
                      color="#e6057d"
                      min="0.25"
                      max="2"
                      step="0.25"
                      style="margin-bottom: 0; margin-top: 20px"
                      thumb-color="#e6057d"
                      thumb-label="always"
                      thumb-size="25"
                      prepend-icon="mdi-play-speed"
                    >
                      <template v-slot:thumb-label="{ value }">
                        {{ `${value}x` }}
                      </template>
                    </v-slider>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </div>
      </div>
    </div>
    <svg style="display: none">
      <defs>
        <symbol id="pause" viewBox="0 0 24 24">
          <path
            d="M14.016 5.016h3.984v13.969h-3.984v-13.969zM6 18.984v-13.969h3.984v13.969h-3.984z"
          ></path>
        </symbol>

        <symbol id="play-icon" viewBox="0 0 24 24">
          <path d="M8.016 5.016l10.969 6.984-10.969 6.984v-13.969z"></path>
        </symbol>

        <symbol id="volume-high" viewBox="0 0 24 24">
          <path
            d="M14.016 3.234q3.047 0.656 5.016 3.117t1.969 5.648-1.969 5.648-5.016 3.117v-2.063q2.203-0.656 3.586-2.484t1.383-4.219-1.383-4.219-3.586-2.484v-2.063zM16.5 12q0 2.813-2.484 4.031v-8.063q1.031 0.516 1.758 1.688t0.727 2.344zM3 9h3.984l5.016-5.016v16.031l-5.016-5.016h-3.984v-6z"
          ></path>
        </symbol>

        <symbol id="volume-low" viewBox="0 0 24 24">
          <path
            d="M5.016 9h3.984l5.016-5.016v16.031l-5.016-5.016h-3.984v-6zM18.516 12q0 2.766-2.531 4.031v-8.063q1.031 0.516 1.781 1.711t0.75 2.32z"
          ></path>
        </symbol>

        <symbol id="volume-mute" viewBox="0 0 24 24">
          <path
            d="M12 3.984v4.219l-2.109-2.109zM4.266 3l16.734 16.734-1.266 1.266-2.063-2.063q-1.547 1.313-3.656 1.828v-2.063q
          1.172-0.328 2.25-1.172l-4.266-4.266v6.75l-5.016-5.016h-3.984v-6h4.734l-4.734-4.734zM18.984 12q0-2.391-1.383-4.219t-3.586-2.484v-2.063q3.047 0.656 5.016 3.117t1.969 5.648q0 2.203-1.031 4.172l-1.5-1.547q0.516-1.266 0.516-2.625zM16.5 12q0 0.422-0.047 0.609l-2.438-2.438v-2.203q1.031 0.516 1.758 1.688t0.727 2.344z"
          ></path>
        </symbol>

        <symbol id="fullscreen" viewBox="0 0 24 24">
          <path
            d="M14.016 5.016h4.969v4.969h-1.969v-3h-3v-1.969zM17.016 17.016v-3h1.969v4.969h-4.969v-1.969h3zM5.016 9.984v-4.969h4.969v1.969h-3v3h-1.969zM6.984 14.016v3h3v1.969h-4.969v-4.969h1.969z"
          ></path>
        </symbol>

        <symbol id="fullscreen-exit" viewBox="0 0 24 24">
          <path
            d="M15.984 8.016h3v1.969h-4.969v-4.969h1.969v3zM14.016 18.984v-4.969h4.969v1.969h-3v3h-1.969zM8.016 8.016v-3h1.969v4.969h-4.969v-1.969h3zM5.016 15.984v-1.969h4.969v4.969h-1.969v-3h-3z"
          ></path>
        </symbol>

        <symbol id="pip" viewBox="0 0 24 24">
          <path
            d="M21 19.031v-14.063h-18v14.063h18zM23.016 18.984q0 0.797-0.609 1.406t-1.406 0.609h-18q-0.797 0-1.406-0.609t-0.609-1.406v-14.016q0-0.797 0.609-1.383t1.406-0.586h18q0.797 0 1.406 0.586t0.609 1.383v14.016zM18.984 11.016v6h-7.969v-6h7.969z"
          ></path>
        </symbol>

        <symbol id="fast-forward" viewBox="0 0 24 24">
          <g id="surface1">
          <path style="fill:none;stroke-width:3;stroke-linecap:round;stroke-linejoin:miter;stroke:white;stroke-opacity:1;stroke-miterlimit:4;" 
          d="M 23.927083 41.40625 L 23.927083 23 C 23.9375 22.958333 23.916667 22.916667 23.875 22.90625 C 23.84375 22.885417 23.791667 
          22.895833 23.770833 22.927083 C 23.770833 22.927083 21.1875 26.625 19.604167 27.708333 " transform="matrix(0.375,0,0,0.375,0,0)"/>
          <path style="fill:none;stroke-width:3;stroke-linecap:butt;stroke-linejoin:miter;stroke:white;stroke-opacity:1;stroke-miterlimit:4;" 
          d="M 34.885417 22.520833 L 34.895833 22.520833 C 38.052083 22.520833 40.604167 25.072917 40.604167 28.21875 L 40.604167 35.708333 
          C 40.604167 38.854167 38.052083 41.40625 34.895833 41.40625 L 34.885417 41.40625 C 31.739583 41.40625 29.1875 38.854167 29.1875 35.708333 L 29.1875 28.21875 C 29.1875 25.072917 31.739583 22.520833 34.885417 22.520833 Z M 34.885417 22.520833 " transform="matrix(0.375,0,0,0.375,0,0)"/>
          <path style="fill:none;stroke-width:6;stroke-linecap:round;stroke-linejoin:miter;stroke:white;stroke-opacity:1;stroke-miterlimit:4;" 
          d="M 54.427083 15.40625 L 51.833333 24.052083 L 43.1875 21.4375 " transform="matrix(0.375,0,0,0.375,0,0)"/>
          <path style="fill:none;stroke-width:6;stroke-linecap:round;stroke-linejoin:miter;stroke:white;stroke-opacity:1;stroke-miterlimit:4;" 
          d="M 51.864583 23.9375 C 47.885417 13.895833 37.1875 8.239583 26.645833 10.625 C 16.114583 13.010417 8.885417 22.71875 9.625 33.5 
          C 10.364583 44.270833 18.854167 52.90625 29.614583 53.822917 C 40.375 54.75 50.208333 47.6875 52.770833 37.1875 " transform="matrix(0.375,0,0,0.375,0,0)"/>
          </g>
        </symbol>

        <symbol id="back-forward" viewBox="0 0 24 24">
          <g id="surface1">
          <path style="stroke-width:6;stroke-linecap:round;stroke-linejoin:miter;stroke:white;stroke-opacity:1;stroke-miterlimit:4;" 
          d="M 9.572917 15.40625 L 12.166667 24.052083 L 20.8125 21.4375 " transform="matrix(0.375,0,0,0.375,0,0)"/>
          <path style="fill:none;stroke-width:3;stroke-linecap:round;stroke-linejoin:miter;stroke:white;stroke-opacity:1;stroke-miterlimit:4;" 
          d="M 26.927083 41.40625 L 26.927083 23 C 26.9375 22.958333 26.916667 22.916667 26.875 22.90625 C 26.84375 22.885417 26.791667 22.895833 26.770833 22.927083 
          C 26.770833 22.927083 24.1875 26.625 22.604167 27.708333 " transform="matrix(0.375,0,0,0.375,0,0)"/>
          <path style="fill:none;stroke-width:3;stroke-linecap:butt;stroke-linejoin:miter;stroke:white;stroke-opacity:1;stroke-miterlimit:4;" 
          d="M 37.885417 22.520833 L 37.895833 22.520833 C 41.052083 22.520833 43.604167 25.072917 43.604167 28.21875 L 43.604167 35.708333 C 43.604167 38.854167 
          41.052083 41.40625 37.895833 41.40625 L 37.885417 41.40625 C 34.739583 41.40625 32.1875 38.854167 32.1875 35.708333 L 32.1875 28.21875 C 32.1875 25.072917 34.739583 22.520833 37.885417 22.520833 Z M 37.885417 22.520833 " transform="matrix(0.375,0,0,0.375,0,0)"/>
          <path style="fill:none;stroke-width:6;stroke-linecap:round;stroke-linejoin:miter;stroke:white;stroke-opacity:1;stroke-miterlimit:4;"
          d="M 12.135417 23.9375 C 16.114583 13.895833 26.8125 8.239583 37.354167 10.625 C 47.885417 13.010417 55.114583 22.71875 54.375 33.5 C 53.635417 44.270833 45.145833 52.90625 34.385417 53.822917 C 23.625 54.75 13.791667 47.6875 11.229167 37.1875 " transform="matrix(0.375,0,0,0.375,0,0)"/>
          </g>
        </symbol>
      </defs>
    </svg>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { userLastVideoDuration, userVideoSeen } from "@/core/utils/user";
import Api from "@/core/services/api.service";
import { getVideoPosterUrl } from "@/core/utils/video";
import { getThemeLogoUrl } from '@/core/utils/theme';
import switchTMG from "@/components/common/switchTMG.vue"
import Swal from "sweetalert2";

export default {
  name: "videoTMG",
  components : {switchTMG},
  props: {
    video: Object,
    nextVideo: Object,
  },
  data: () => ({
    // Elements du DOM
    videoDom : null,
    videoControls : null,
    playButton : null,
    playbackIcons : null,
    timeElapsed : null,
    duration : null,
    progressBar : null,
    seek : null,
    seekTooltip : null,
    volumeButton : null,
    volumeIcons : null,
    volumeMute : null,
    volumeLow : null,
    volumeHigh : null,
    volume : null,
    playbackAnimation : null,
    fullscreenButton : null,
    videoContainer : null,
    fullscreenIcons : null,
    pipButton : null,
    fastForwardButton : null,
    backForwardButton : null,
    nextVideoPreview : null,
    lastProgressResumeVisible : false,
    optionsMenuContent: null,
    displayCountdownTimer : null,
    browserPrefixes : ["", "moz", "webkit", "ms"],

    // Gestion de la vidéo
    speedVideo: 1,
    autoplayEnabled: false,
    isFullscreen: false,
    mouseOnControls: false,
    mouseOnVideo: false,
    userInteracted : false,
    hasBeenPlayed : false,
    seen : false,
    videoUser: null,
    videoProgress: 0,
    videoDuration: 0,
    lastUserVideoDuration: 0,

    // Compteur de fin de vidéo
    countdown : null,
    countdownTimer : 10,
    countdownInProgress : false,
    hideControlTimeout : null,
  }),
  computed: {
    ...mapGetters(["layoutConfig","currentUser"]),
    isFirefox : function (){
      return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    },
    videoPoster: function () {
      return getVideoPosterUrl(this.video);
    },
    nextVideoPoster: function () {
      if (this.nextVideo) {
        return getVideoPosterUrl(this.nextVideo);
      }
      return null;
    },
    themeLogo : function () {
      return getThemeLogoUrl(this.nextVideo ? this.nextVideo.theme: null, process.env.BASE_URL + this.layoutConfig("self.nologo"));
    },
    apiUrl: function () {
      return process.env.VUE_APP_API;
    },
  },
  methods: {
    // #######################################################################
    // Méthodes globales
    // #######################################################################
    /**
     * Chargement de la vidéo suivante
     */
    loadNextVideo : function () {
      if (this.nextVideo && this.nextVideo.route && this.nextVideo.route.name){
        this.$router.push(this.nextVideo.route);
      }
    },
    /**
     * Modification de la vitesse de la vidéo
     */
    speedVideoChange: function () {
      this.videoDom.playbackRate = parseFloat(this.speedVideo);
    },

    /**
     * Méthode d'avance de 10 secondes dans la vidéo
     */
    fastForward: function () {
      this.videoDom.currentTime += 10;
    },
    /**
     * Méthode de recul de 10 secondes dans la vidéo
     */
    backForward: function () {
      this.videoDom.currentTime -= 10;
    },
    /**
     * Méthode permettant de lancer automatiquement la vidéo si souhaité
     */
    autoplayVideo: function () {
      // Vérification si autoplay actif lancement de la vidéo
      if (this.autoplayEnabled && !this.hasBeenPlayed) {
        this.videoDom.muted = true;
        this.togglePlay();
        this.videoDom.muted = false;
      }
    },
    /**
     * Méthode permettant de gèrer le statut de lecture de la vidéo.
     * si une vidéo est en pause ou terminé, la vidéo est lancée
     * sinon elle est mise en pause
     */
    togglePlay: function () {
      if (this.videoDom.paused || this.videoDom.ended) {
        this.hideVideoPreview();
        this.videoDom.play();
      } else {
        this.videoDom.pause();
      }
    },
    /**
     * Mise à jour des icônes et du tooltip en fonction de l'état de la vidéo
     */
    updatePlayButton: function () {
      this.playbackIcons.forEach((icon) => icon.classList.toggle("hidden"));

      if (this.videoDom.paused) {
        this.playButton.setAttribute("data-title", `${this.$i18n.t('video.player.play')} (${this.$i18n.t('video.player.shortcut.space')})`);
      } else {
        this.playButton.setAttribute("data-title", `${this.$i18n.t('video.player.pause')} (${this.$i18n.t('video.player.shortcut.space')})`);
      }
    },

    /**
     * Formattage du compteur
     */
    formatTime(timeInSeconds) {
      const result = new Date(timeInSeconds * 1000).toISOString().substr(11, 8);

      return {
        minutes: result.substr(3, 2),
        seconds: result.substr(6, 2),
      };
    },

    // initializeVideo sets the video duration, and maximum value of the
    // progressBar
    initializeVideo: function () {
      this.videoDuration = Math.round(this.videoDom.duration);
      this.seek.setAttribute("max", this.videoDuration);
      this.progressBar.setAttribute("max", this.videoDuration);
      const time = this.formatTime(this.videoDuration);
      this.duration.innerText = `${time.minutes}:${time.seconds}`;
      this.duration.setAttribute("datetime", `${time.minutes}m ${time.seconds}s`);
      this.updateTolastUserVideoDuration();
      this.updateVolume();
      this.updateVolumeIcon();
    },

    updateTolastUserVideoDuration: function () {
      this.lastUserVideoDuration = userLastVideoDuration(
        this.currentUser,
        this.video,
        this.videoDuration
      );
      if (this.lastUserVideoDuration == this.videoDuration){
        this.currentTime = 0;
      }
      else if (this.lastUserVideoDuration && this.lastUserVideoDuration > 0) {
        this.videoDom.currentTime = this.lastUserVideoDuration;
        this.lastProgressResumeVisible = true;
      }
    },

    // updateTimeElapsed indicates how far through the video
    // the current playback is by updating the timeElapsed element
    updateTimeElapsed: function () {
      const time = this.formatTime(Math.round(this.videoDom.currentTime));
      this.timeElapsed.innerText = `${time.minutes}:${time.seconds}`;
      this.timeElapsed.setAttribute("datetime", `${time.minutes}m ${time.seconds}s`);
    },

    // updateProgress indicates how far through the video
    // the current playback is by updating the progress bar
    updateProgress: function () {
      this.seek.value = Math.floor(this.videoDom.currentTime);
      this.progressBar.value = Math.floor(this.videoDom.currentTime);
      this.videoProgress = Math.round((this.videoDom.currentTime * 100) / this.videoDom.duration); // Calcul du pourcentage
    },

    // updateSeekTooltip uses the position of the mouse on the progress bar to
    // roughly work out what point in the video the user will skip to if
    // the progress bar is clicked at that point
    updateSeekTooltip(event) {
      const skipTo = Math.round(
        (event.offsetX / event.target.clientWidth) *
          parseInt(event.target.getAttribute("max"), 10)
      );
      this.seek.setAttribute("data-seek", skipTo);
      const t = this.formatTime(skipTo);
      this.seekTooltip.textContent = `${t.minutes}:${t.seconds}`;
      const rect = this.videoDom.getBoundingClientRect();
      this.seekTooltip.style.left = `${event.pageX - rect.left}px`;
    },

    // skipAhead jumps to a different point in the video when the progress bar
    // is clicked
    skipAhead(event) {
      const skipTo = event.target.dataset.seek
        ? event.target.dataset.seek
        : event.target.value;
      this.videoDom.currentTime = skipTo;
      this.progressBar.value = skipTo;
      this.seek.value = skipTo;
    },

    // updateVolume updates the video's volume
    // and disables the muted state if active
    updateVolume: function () {
      if (this.videoDom.muted) {
        this.videoDom.muted = false;
      }
      this.videoDom.volume = this.volume.value;
    },

    /**
     * Méthode pour augmenter le volume 
     */
    volumeUp: function () {
      this.videoDom.volume = this.videoDom.volume < 0.9 ? this.videoDom.volume + 0.1 : 1;
      this.volume.value = this.videoDom.volume;
      this.updateVolume();
      this.updateVolumeIcon();
    },

    /**
     * Méthode pour diminuer le volume 
     */
    volumeDown(){
      this.videoDom.volume = this.videoDom.volume > 0.1 ? this.videoDom.volume - 0.1 : 0;
      this.volume.value = this.videoDom.volume;
      this.updateVolume();
      this.updateVolumeIcon();
    },

    // updateVolumeIcon updates the volume icon so that it correctly reflects
    // the volume of the video
    updateVolumeIcon: function () {
      this.volumeIcons.forEach((icon) => {
        icon.classList.add("hidden");
      });

      this.volumeButton.setAttribute("data-title", `${this.$t('video.player.unmute')} (m)`);

      if (this.videoDom.muted || this.videoDom.volume === 0) {
        this.volumeMute.classList.remove("hidden");
        this.volumeButton.setAttribute("data-title", `${this.$t('video.player.mute')} (m)`);
      } else if (this.videoDom.volume > 0 && this.videoDom.volume <= 0.5) {
        this.volumeLow.classList.remove("hidden");
      } else {
        this.volumeHigh.classList.remove("hidden");
      }
    },

    // toggleMute mutes or unmutes the video when executed
    // When the video is unmuted, the volume is returned to the value
    // it was set to before the video was muted
    toggleMute: function () {
      this.videoDom.muted = !this.videoDom.muted;

      if (this.videoDom.muted) {
        this.volume.setAttribute("data-volume", this.volume.value);
        this.volume.value = 0;
      } else {
        this.volume.value = this.volume.dataset.volume;
      }
    },

    // animatePlayback displays an animation when
    // the video is played or paused
    animatePlayback: function () {
      this.playbackAnimation.animate(
        [
          {
            opacity: 1,
            transform: "scale(1)",
          },
          {
            opacity: 0,
            transform: "scale(1.3)",
          },
        ],
        {
          duration: 500,
        }
      );
    },

    // toggleFullScreen toggles the full screen state of the video
    // If the browser is currently in fullscreen mode,
    // then it should exit and vice versa.
    toggleFullScreen: function () {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (document.webkitFullscreenElement) {
        // Need this to support Safari
        document.webkitExitFullscreen();
      } else if (this.videoContainer.webkitRequestFullscreen) {
        // Need this to support Safari
        this.videoContainer.webkitRequestFullscreen();
      } else {
        this.videoContainer.requestFullscreen();
      }
    },

    // updateFullscreenButton changes the icon of the full screen button
    // and tooltip to reflect the current full screen state of the video
    updateFullscreenButton: function () {
      this.muted = document.fullscreenElement !== this;
      this.fullscreenIcons.forEach((icon) => icon.classList.toggle("hidden"));

      if (document.fullscreenElement) {
        this.fullscreenButton.setAttribute("data-title", `${this.$t('video.player.fullscreen')} (f)`);
      } else {
        this.fullscreenButton.setAttribute("data-title", `${this.$t('video.player.exitFullscreen')} (f)`);
      }
    },


    // togglePip toggles Picture-in-Picture mode on the video
    async togglePip() {
      try {
        if(this.isFirefox){
          Swal.fire({
            position: 'top-end',
            title: this.$i18n.t('video.pipNotSupported.title'),
            html: this.$i18n.t('video.pipNotSupported.html'),
            showCloseButton: true,
            confirmButtonText:'<i class="fa fa-thumbs-up"></i> Ok !',
          });
          return;
        }
        if (this.videoDom !== document.pictureInPictureElement) {
          this.pipButton.disabled = true;
          await this.videoDom.requestPictureInPicture();
        } else {
          await document.exitPictureInPicture();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.pipButton.disabled = false;
      }
    },

    /**
     * Méthode de vérification si le menu d'option est actif
     */
    optionMenuIsActive: function () {
      return (
        this.optionsMenuContent[0] &&
        this.optionsMenuContent[0].classList.contains("menuable__content__active")
      );
    },

    /**
     * Méthode de démarrage du compteur pour la prochaine vidéo
     */
    startCountdownNextVideo: function () {
      let self = this;
      if(this.countdown == null){
        this.countdown = setInterval(function () {
          self.countdownInProgress = true;
          self.displayCountdownTimer.innerHTML = `${String(self.countdownTimer).padStart(2,'0')} ${self.$t('video.player.nextVideoSeconds')}`;
          if (self.countdownTimer <= 0 && !document.hidden) {
            clearInterval(self.countdown);
            self.loadNextVideo();
          } else {
            self.countdownTimer--;
          }
        }, 1000);
      }
    },
    pauseCountdownNextVideo: function() {
      clearInterval(this.countdown);
      this.countdown = null;
    },
    /**
     * Méthode de réintialisation du compteur pour la prochaine vidéo
     */
    resetCountdownNextVideo: function () {
      this.countdownInProgress = false;
      this.countdownTimer = 10;
      clearInterval(this.countdown);
      this.countdown = null;
      this.displayCountdownTimer.innerHTML = `${String(this.countdownTimer).padStart(2, '0')} ${this.$t('video.player.nextVideoSeconds')}`;
    },

    /**
     * Méthode permettant de gèrer les raccourcis clavier
     */
    keyboardShortcuts: function (event) {
      const { key, code } = event;
      let self = this;
      // Play ==> Space 
      if(code == 'Space' || key == ' '){
          event.preventDefault();
          this.togglePlay();
          this.animatePlayback();
          if (this.videoDom.paused) {
            this.showControls();
          } else {
            setTimeout(() => {
              self.hideControls();
            }, 2000);
          }
      }
      // Fullscreen ==> f 
      else if(key == 'f' || key == 'F'){
          this.toggleFullScreen();
      }
      // PIP ==> p 
      else if(key == 'p' || key == 'P'){
          if(!this.isFirefox){
            this.togglePip();
          }
      }
      // Avance Xs ==> Fleche droite
      else if(key == 'ArrowRight'){
          this.fastForward();
      }
      // Retour Xs ==> Fleche gauche 
      else if(key == 'ArrowLeft'){
          this.backForward();
      }
      // Mute ==> m
      else if(key == "m" || key == 'M'){
          this.toggleMute();
      }
      // Volume + ==> +
      else if(key == '+' || key == 'AudioVolumeUp'){
          this.volumeUp();
      }
      // Volume - ==> - (40)
      else if(key == '-' || key == 'AudioVolumeDown'){
        this.volumeDown();
      }
    },

    // hideControls hides the video controls when not in use
    // if the video is paused, the controls must remain visible
    hideControls: function (onTimeout = false) {
      let self = this;
      // Gestion des cas de sortie
      if (this.optionMenuIsActive() || this.videoDom.ended) {
        clearTimeout(this.hideControlTimeout);
        return;
      }
      // Gestion d'une demande de masquage avec délai
      if (onTimeout && this.mouseOnVideo) {
        clearTimeout(this.hideControlTimeout);
        this.showControls();
        this.hideControlTimeout = setTimeout(function () {
          self.hideControls();
        }, 2000);
        return;
      }
      // Masquage des contrôles
      this.videoControls.classList.add("hide");
      document.getElementById("video-controls");
    },

    /**
     * showControls displays the video controls 
     * */ 
    showControls: function () {
      this.videoControls.classList.remove("hide");
    },

    hideLastProgressResumeMessage : function () {
      setTimeout(() => {
              this.lastProgressResumeVisible = false;
          }, 3000);
    },

    showVideoPreview: function () {
      this.showControls();
      if(this.nextVideo && this.nextVideo.route){
        this.nextVideoPreview.classList.remove("hidden");
        this.startCountdownNextVideo();
      }
    },

    hideVideoPreview: function () {
      this.resetCountdownNextVideo();
      this.nextVideoPreview.classList.add("hidden");
    },

    hidePipButton: function () {
      if (!("pictureInPictureEnabled" in document)) {
        this.pipButton.classList.add("hidden");
      }
    },
    // #######################################################################
    // Gestion des événements VIDEO
    // Prolongement des événenements natifs + Comportement spécifique TMG
    // #######################################################################

    video_play : function (event) {
      this.$emit("play", event);
      this.hideLastProgressResumeMessage();
      this.addVideoView();
      this.updatePlayButton(); 
    },
    video_pause: function (event) {
      this.$emit("pause", event);
      this.updatePlayButton();
    },
    video_ended: function (event) {
      this.$emit("ended", event);
      this.showVideoPreview();
      this.updateTimeElapsed();
      this.updateProgress();
    },
    video_loadedmetadata: function (event) {
      this.$emit("loadedmetadata", event);
      this.initializeVideo();
    },
    video_loadeddata: function (event) {
      this.$emit("loadeddata", event);
      this.autoplayVideo();
    },
    video_volumechange: function (event) {
      this.$emit("volumechange", event);
      this.updateVolumeIcon();
    },
    video_click: function (event) {
      this.$emit("click", event);
      this.togglePlay();
      this.animatePlayback();
    },
    video_mouseenter: function (event) {
      this.$emit("mouseenter", event);
      this.mouseOnVideo = true;
      this.showControls();
    },
    video_mouseleave: function (event) {
      this.$emit("mouseleave", event);
      this.mouseOnVideo = false;
      this.hideControls();
    },
    video_mousemove: function (event) {
      this.$emit("mousemove", event);
      this.mouseOnVideo = true;
      this.hideControls(true);
    },
    video_timeupdate: function (event) {
      this.$emit("timeupdate", event);
      // Mise à jour des informations et des progrès utilisateur
      this.updateTimeElapsed();
      this.updateProgress();
      // Vérification si masquage de la preview à faire
      if(!this.videoDom.ended){
        this.hideVideoPreview();
      }
    },
    video_durationchange: function (event) {
      this.$emit("durationchange", event);
    },
    // #######################################################################
    // Gestion des événements du contrôleurs de la vidéo
    // #######################################################################
    videoControls_mouseenter: function () {
      this.mouseOnControls = true;
      this.showControls();
    },
    videoControls_mouseleave: function () {
      this.mouseOnControls = false;
      this.hideControls();
    },
    // #######################################################################
    // Méthodes API 
    // #######################################################################
    /**
     * Définition de l'autoplay pour l'utilisateur courant
     */
    autoplayChange: function () {
      Api.patch("users", this.currentUser["id"], {
        autoplay: this.autoplayEnabled,
      }).catch((err) => {
        console.log(err);
      });
    },
    /**
     * Définition de la progression de l'utilisateur pour la vidéo courante
     */
    saveUserVideoProgress() {
      try{
        // Vérification si mise à jour nécessaire
        if (this.videoProgress <= 0) {
          return;
        }
        if(this.videoUser != null){
          // Gestion de la progression de la vidéo
          let params = {lastProgress : this.videoProgress};
          if(this.videoProgress > this.videoUser.progress) {
            params.progress = this.videoProgress;
          }
          Api.patch('video_users', this.videoUser.id, params);
        }
      }catch(error){
        console.debug("Impossible d'enregistrer la progression de la vidéo");
      }
    },

    /**
     * Ajout d'une vue à la vidéo pour l'utilisateur et la vidéo courante
     */
    addVideoView() {
      let self = this;
      if(!this.hasBeenPlayed){
        this.hasBeenPlayed = true;
        if (this.videoUser) {
          Api.patch('video_users', this.videoUser.id, {views: this.videoUser.views + 1});
        }
        else{
          Api.post('video_users', {
            user: this.currentUser['@id'],
            video: this.video['@id'],
            progress: 0,
            lastProgress: 0,
            views : 1
          }).then(({data}) => {
            self.videoUser = data;
          });
        }
      }
    },
    handleVisibilityChange: function () {
      if (document.hidden && this.countdownInProgress) {
          this.pauseCountdownNextVideo()
      } else if (!document.hidden && this.countdown == null && this.countdownInProgress){
          this.startCountdownNextVideo();
      }
    },
  },
  mounted() {
    let self = this;
    
    // Récupèration des configurations utilisateurs
    this.videoUser = this.currentUser.userVideos.find(uv => uv.video === this.video['@id']);
    this.autoplayEnabled = this.currentUser["autoplay"];
    this.seen = userVideoSeen(this.currentUser, this.video);

    // #######################################################################
    // Définition des variables locales
    // #######################################################################
    // Contrôles de la vidéos
    this.videoDom = document.getElementById("videoDom");
    this.videoControls = document.getElementById("video-controls");
    this.playButton = document.getElementById("play");
    this.playbackIcons = document.querySelectorAll(".playback-icons use");
    this.timeElapsed = document.getElementById("time-elapsed");
    this.duration = document.getElementById("duration");
    this.progressBar = document.getElementById("progress-bar");
    this.seek = document.getElementById("seek");
    this.seekTooltip = document.getElementById("seek-tooltip");
    this.volumeButton = document.getElementById("volume-button");
    this.volumeIcons = document.querySelectorAll(".volume-button use");
    this.volumeMute = document.querySelector('use[href="#volume-mute"]');
    this.volumeLow = document.querySelector('use[href="#volume-low"]');
    this.volumeHigh = document.querySelector('use[href="#volume-high"]');
    this.volume = document.getElementById("volume");
    this.playbackAnimation = document.getElementById("playback-animation");
    this.fullscreenButton = document.getElementById("fullscreen-button");
    this.videoContainer = document.getElementById("video-container");
    this.fullscreenIcons = this.fullscreenButton.querySelectorAll("use");
    this.pipButton = document.getElementById("pip-button");
    this.fastForwardButton = document.getElementById("fast-forward-button");
    this.backForwardButton = document.getElementById("back-forward-button");
    this.nextVideoPreview = document.getElementById("next-video-preview");
    this.optionsMenuContent = document.getElementsByClassName(
      "options-menu-content"
    );
    this.displayCountdownTimer = document.getElementById(
      "message-preview-title-info"
    );
    // Vidéos
    this.videoWorks = !!document.createElement("video").canPlayType;
    if (this.videoWorks) {
      this.videoDom.controls = false;
      this.videoControls.classList.remove("hidden");
    }

    // Compteur de fin de vidéo
    this.countdown = null;
    this.countdownTimer = 10;
    this.hideControlTimeout = null;
    this.countdownInProgress = false;
    
    // Ajout des évenéments
    document.addEventListener("DOMContentLoaded", this.hidePipButton);
    document.addEventListener("keydown", this.keyboardShortcuts);
    this.fullscreenButton.addEventListener("click", this.toggleFullScreen);
    this.browserPrefixes.forEach(function (prefix) {
      self.videoDom.addEventListener(prefix + "fullscreenchange", self.updateFullscreenButton);
    });
    // Vérification de la visibilité 
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeDestroy(){
    let self = this;

    // Suppression des évenéments
    document.removeEventListener("DOMContentLoaded", this.hidePipButton);
    document.removeEventListener("keydown", this.keyboardShortcuts);
    this.fullscreenButton.removeEventListener("click", this.toggleFullScreen);
    this.browserPrefixes.forEach(function (prefix) {
      self.videoDom.removeEventListener(prefix + "fullscreenchange", self.updateFullscreenButton);
    });
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Mise à jour de la progression de la vidéo
    this.saveUserVideoProgress(); 

    // Suppression des Interval/Timemout
    clearTimeout(this.hideControlTimeout);
    clearInterval(self.countdown);

    // Suppression de la source de la vidéo
    this.videoDom.pause();
    this.videoDom.removeAttribute('src');
    this.videoDom.load();
  }
};
</script>